import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import FeatureIcon from '../../components/FeatureIcon';
import StatusPlatformIcon from './StatusPlatformIcon';
import StatusCategoryMarker from './StatusCategoryMarker';
import { ChangelogPlatform } from '../../data/changelog';
import { ChangelogEntry, formatDate } from './statusData';

interface ReleaseHistoryProps {
  entries: ChangelogEntry[];
  platformTotal: number;
  hasAnyEntries: boolean;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  selectedPlatform: ChangelogPlatform;
  onPlatformChange: (platform: ChangelogPlatform) => void;
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  selectedBuild: string;
  onBuildChange: (build: string) => void;
  buildVersions: string[];
  showCategoryDropdown: boolean;
  showBuildDropdown: boolean;
  onToggleCategory: () => void;
  onToggleBuild: () => void;
  onCloseDropdowns: () => void;
}

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'feature', label: 'Features' },
  { value: 'improvement', label: 'Improvements' },
  { value: 'bugfix', label: 'Bug Fixes' },
  { value: 'security', label: 'Security' },
];

const ReleaseHistory: React.FC<ReleaseHistoryProps> = ({
  entries,
  platformTotal,
  hasAnyEntries,
  loading,
  error,
  onRetry,
  selectedPlatform,
  onPlatformChange,
  selectedCategory,
  onCategoryChange,
  selectedBuild,
  onBuildChange,
  buildVersions,
  showCategoryDropdown,
  showBuildDropdown,
  onToggleCategory,
  onToggleBuild,
  onCloseDropdowns,
}) => {
  const { colors, dsColors } = useTheme();
  const filterRef = useRef<HTMLDivElement>(null);

  // Close open dropdowns on outside click or Escape.
  useEffect(() => {
    if (!showCategoryDropdown && !showBuildDropdown) return;
    const onDocClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        onCloseDropdowns();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseDropdowns();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showCategoryDropdown, showBuildDropdown, onCloseDropdowns]);

  const categoryLabel =
    selectedCategory === 'all'
      ? 'All'
      : selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1);

  const optionStyle = (selected: boolean): React.CSSProperties => ({
    backgroundColor: selected ? colors.surfaceSecondary : 'transparent',
    color: selected ? colors.textPrimary : colors.textSecondary,
  });

  return (
    <section aria-labelledby="status-releases-heading" className="status-panel">
      <h2
        id="status-releases-heading"
        className="status-panel-heading status-panel-heading-lg"
        style={{ color: colors.textPrimary }}
      >
        <FeatureIcon name="clock" size={20} className="status-panel-icon" />
        Release history
      </h2>
      <p className="text-sm mb-5" style={{ color: colors.textSecondary }}>
        Track new features, improvements, and fixes across all builds
      </p>

      <div className="status-filters" ref={filterRef}>
        {/* Platform toggle — exactly two options, iOS / Android, no combined view */}
        <div
          className="status-platform-toggle"
          role="group"
          aria-label="Platform"
          style={{ borderColor: colors.border }}
        >
          {(['ios', 'android'] as ChangelogPlatform[]).map((platform) => {
            const selected = selectedPlatform === platform;
            return (
              <button
                key={platform}
                type="button"
                aria-pressed={selected}
                onClick={() => onPlatformChange(platform)}
                className="status-platform-btn"
                style={{
                  backgroundColor: selected ? colors.surfaceSecondary : 'transparent',
                  color: colors.textPrimary,
                  fontWeight: selected ? 600 : 400,
                  boxShadow: selected ? `inset 0 0 0 2px ${dsColors.signalBlue}` : 'none',
                }}
              >
                <StatusPlatformIcon platform={platform} />
                {platform === 'ios' ? 'iOS' : 'Android'}
              </button>
            );
          })}
        </div>

        {/* Type dropdown */}
        <div className="relative">
          <button
            type="button"
            aria-expanded={showCategoryDropdown}
            aria-controls="status-category-dropdown"
            onClick={onToggleCategory}
            className="status-filter-btn"
            style={{
              color: colors.textPrimary,
              borderColor: selectedCategory !== 'all' ? colors.textSecondary : colors.border,
            }}
          >
            <span>Type: {categoryLabel}</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showCategoryDropdown && (
            <div
              id="status-category-dropdown"
              className="status-dropdown"
              style={{ backgroundColor: colors.surface, borderColor: colors.border }}
            >
              {CATEGORY_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={selectedCategory === value}
                  onClick={() => {
                    onCategoryChange(value);
                    onCloseDropdowns();
                  }}
                  className="status-option"
                  style={optionStyle(selectedCategory === value)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Build dropdown */}
        <div className="relative">
          <button
            type="button"
            aria-expanded={showBuildDropdown}
            aria-controls="status-build-dropdown"
            onClick={onToggleBuild}
            className="status-filter-btn"
            style={{
              color: colors.textPrimary,
              borderColor: selectedBuild !== 'all' ? colors.textSecondary : colors.border,
            }}
          >
            <span>Build: {selectedBuild === 'all' ? 'All' : selectedBuild}</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showBuildDropdown && (
            <div
              id="status-build-dropdown"
              className="status-dropdown"
              style={{ backgroundColor: colors.surface, borderColor: colors.border }}
            >
              <button
                type="button"
                aria-pressed={selectedBuild === 'all'}
                onClick={() => {
                  onBuildChange('all');
                  onCloseDropdowns();
                }}
                className="status-option"
                style={optionStyle(selectedBuild === 'all')}
              >
                All Builds
              </button>
              {buildVersions.map((version) => (
                <button
                  key={version}
                  type="button"
                  aria-pressed={selectedBuild === version}
                  onClick={() => {
                    onBuildChange(version);
                    onCloseDropdowns();
                  }}
                  className="status-option"
                  style={optionStyle(selectedBuild === version)}
                >
                  {version}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="status-results-count" style={{ color: colors.textSecondary }}>
          {entries.length} of {platformTotal}
        </div>

        {(selectedCategory !== 'all' || selectedBuild !== 'all') && (
          <button
            type="button"
            onClick={() => {
              onCategoryChange('all');
              onBuildChange('all');
            }}
            className="status-clear-btn"
            style={{ color: colors.textPrimary, borderColor: colors.border }}
          >
            Clear
          </button>
        )}
      </div>

      <div className="status-release-list">
        {loading ? (
          <p className="py-8 text-center" style={{ color: colors.textSecondary }}>
            Loading changelog...
          </p>
        ) : error ? (
          <div className="py-8 text-center">
            <p className="mb-3" style={{ color: colors.textSecondary }}>
              Unable to load the changelog. Please try again.
            </p>
            <button
              type="button"
              onClick={onRetry}
              className="status-retry-btn"
              style={{ color: colors.textPrimary, borderColor: colors.border }}
            >
              Retry
            </button>
          </div>
        ) : entries.length === 0 ? (
          <p className="py-8 text-center" style={{ color: colors.textSecondary }}>
            {hasAnyEntries ? 'No entries match the selected filters' : 'No changelog entries yet'}
          </p>
        ) : (
          entries.map((entry) => {
            const validDate = !isNaN(new Date(entry.published_at).getTime());
            return (
            <article
              key={entry.id}
              className="status-release-row"
              style={{ borderColor: colors.border }}
            >
              <div className="status-release-head">
                <span className="status-release-version" style={{ color: colors.textPrimary }}>
                  v{entry.version}
                </span>
                {validDate ? (
                  <time className="status-release-date" dateTime={entry.published_at} style={{ color: colors.textSecondary }}>
                    {formatDate(entry.published_at)}
                  </time>
                ) : (
                  <span className="status-release-date" style={{ color: colors.textSecondary }}>
                    {formatDate(entry.published_at)}
                  </span>
                )}
                <StatusCategoryMarker category={entry.category} />
              </div>
              <h3 className="status-release-title" style={{ color: colors.textPrimary }}>
                {entry.title}
              </h3>
              {entry.description.includes('•') ? (
                <ul className="list-none space-y-1" style={{ color: colors.textSecondary }}>
                  {entry.description.split('•').filter((item) => item.trim()).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-sm mt-1" aria-hidden="true">•</span>
                      <span className="flex-1 min-w-0">{item.trim()}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p style={{ color: colors.textSecondary }}>{entry.description}</p>
              )}
            </article>
            );
          })
        )}
      </div>
    </section>
  );
};

export default ReleaseHistory;
